import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  Typography,
} from "@suraasa/placebo-ui"
import { DeliveryMode } from "api/resources/learningItems/types"
import { PropsWithChildren, useEffect, useRef, useState } from "react"
import {
  BROWSER_STORAGE_KEYS,
  IS_DEV_ENVIRONMENT,
  IS_LOCALHOST,
} from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { isShadow } from "utils/shadow"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
import { UserPreferenceManager } from "utils/userPreferenceManager"

import { getPgCTLProgramName } from "../PgCTLEnrollmentAgreement/utils"

// 22 Jun 2026 00:00 IST. Learners who accepted the enrollment agreement
// before this cutoff are existing learners and must not be forced to watch.
const GO_LIVE_DATE = new Date("2026-06-21T18:30:00Z")

const VIDEO_URLS: Partial<Record<DeliveryMode, string>> = {
  [DeliveryMode.STARTER]:
    "https://assets.suraasa.com/lms/pgctl-orientation-videos/pgctl-starter.mp4",
  [DeliveryMode.PROFESSIONAL]:
    "https://assets.suraasa.com/lms/pgctl-orientation-videos/pgctl-professional.mp4",
}

const VIDEO_POSTERS: Partial<Record<DeliveryMode, string>> = {
  [DeliveryMode.STARTER]:
    "https://assets.suraasa.com/lms/pgctl-orientation-videos/pgctl-starter-poster.webp",
  [DeliveryMode.PROFESSIONAL]:
    "https://assets.suraasa.com/lms/pgctl-orientation-videos/pgctl-professional-poster.webp",
}

// Fraction of the video that must be watched to unlock the Proceed button.
const WATCH_THRESHOLD = 0.9

const PgCTLOrientationVideo = (props: PropsWithChildren) => {
  const {
    pgctl: { deliveryMode },
  } = useEnrollments()

  const videoUrl = deliveryMode ? VIDEO_URLS[deliveryMode] : undefined
  const programName = (deliveryMode && getPgCTLProgramName(deliveryMode)) || {
    shortName: "",
    modeDisplay: "",
  }

  const agreementAcceptedAt = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.pgctlEnrollmentAgreementAccepted
  )
  const videoCompleted = !!UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.pgctlOrientationVideoCompleted
  )

  const shouldShow =
    !!videoUrl &&
    !videoCompleted &&
    !!agreementAcceptedAt &&
    new Date(agreementAcceptedAt) >= GO_LIVE_DATE

  const [dismissed, setDismissed] = useState(false)
  const [canFinish, setCanFinish] = useState(false)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const presentedRef = useRef(false)
  const startedRef = useRef(false)
  const completedRef = useRef(false)
  const milestonesRef = useRef<Set<number>>(new Set())
  const watchtimeRef = useRef(0)
  const lastTimeRef = useRef(0)

  const eventData = (extra?: object) => ({
    productName: programName.shortName,
    product_name: programName.shortName,
    delivery_mode: deliveryMode,
    ...extra,
  })

  const track = (eventName: string, extra?: object) => {
    trackingService.trackEvent(eventName, eventData(extra), {
      services: ["PostHog"],
    })
  }

  useEffect(() => {
    if (shouldShow && !dismissed && !presentedRef.current) {
      presentedRef.current = true
      track(TrackingEvents.orientationVideoPresented)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, dismissed])

  const handlePlay = () => {
    if (!startedRef.current) {
      startedRef.current = true
      track(TrackingEvents.orientationVideoStarted)
    }
  }

  // Unlocks the Proceed button + fires the completed event once, when the
  // learner has reached the watch threshold (or the video ends).
  const markComplete = () => {
    if (completedRef.current) return
    completedRef.current = true
    setCanFinish(true)
    track(TrackingEvents.orientationVideoCompleted, {
      watchtime_seconds: Math.round(watchtimeRef.current),
      duration_seconds: Math.round(videoRef.current?.duration ?? 0),
    })
  }

  const handleTimeUpdate = () => {
    const video = videoRef.current
    if (!video || !video.duration) return

    // Accumulate real watchtime, ignoring forward seeks (delta > 2s).
    const delta = video.currentTime - lastTimeRef.current
    if (delta > 0 && delta < 2) watchtimeRef.current += delta
    lastTimeRef.current = video.currentTime

    const pct = (video.currentTime / video.duration) * 100
    ;[5, 15, 25, 50, 75, 90, 95, 100].forEach(milestone => {
      if (pct >= milestone && !milestonesRef.current.has(milestone)) {
        milestonesRef.current.add(milestone)
        track(TrackingEvents.orientationVideoProgress, {
          milestone,
          watchtime_seconds: Math.round(watchtimeRef.current),
        })
      }
    })

    if (pct >= WATCH_THRESHOLD * 100) markComplete()
  }

  const handleEnded = () => markComplete()

  const handleFinish = () => {
    UserPreferenceManager.set(
      BROWSER_STORAGE_KEYS.pgctlOrientationVideoCompleted,
      new Date().toISOString()
    )
    setDismissed(true)
  }

  const allowDismiss = isShadow() || IS_LOCALHOST || IS_DEV_ENVIRONMENT

  if (!shouldShow || dismissed) return props.children

  return (
    <Sheet
      open
      onOpenChange={open => {
        if (!open && allowDismiss) setDismissed(true)
      }}
    >
      <SheetContent height={95} className="p-0">
        <SheetHeader
          hideClose={!allowDismiss}
          className="z-1 items-center pb-2"
        >
          <SheetTitle className="items-center">
            <img
              src="https://assets.suraasa.com/logos/v2/full-dark.webp"
              alt="logo"
              width={150}
            />
          </SheetTitle>
        </SheetHeader>

        <SheetBody className="flex h-full flex-col gap-2 px-2 py-0 sm:px-[38px]">
          <div className="flex flex-col gap-0.5">
            <Typography variant="title3">
              Welcome to {programName.shortName}
            </Typography>
            <Typography variant="body">
              Watch this orientation video to unlock access to your program.
            </Typography>
          </div>

          <div className="flex-1 rounded-xl">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              className="aspect-video w-full"
              controls
              controlsList="nodownload"
              src={videoUrl}
              poster={deliveryMode ? VIDEO_POSTERS[deliveryMode] : undefined}
              onPlay={handlePlay}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              playsInline
            />
          </div>

          <div className="sticky bottom-0 flex justify-end bg-white/80 p-2 backdrop-blur-sm">
            <Tooltip
              className="overflow-hidden"
              content="Please watch the video to unlock access to your program."
              enabled={!canFinish}
            >
              <Button
                onClick={handleFinish}
                disabled={!canFinish}
                color="black"
              >
                Proceed to {programName.shortName}
              </Button>
            </Tooltip>
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

export default PgCTLOrientationVideo
